<template lang="pug">
  .chat_header
    v-layout.contact_item(:class="{ special: chat.type_id === chatTypes.special }", justify-space-between, align-start)
      v-flex
        v-layout(align-center)
          .contact_back(@click="$emit('clickBack')")
            SvgArrowThin
          .contact_preview(v-if="chat.type_id === chatTypes.private && chat.contact")
            div(v-if="chat.contact.avatar_url", :style="{ 'background-image': 'url(' + chat.contact.avatar_url + ')' }")
            AvatarPlaceholder.md(v-else, :fullName="chat.contact.first_name + ' ' + chat.contact.last_name", :classname="getAvatarClass(chat.contact.first_name, chat.contact.last_name)")
          .contact_preview(v-else)
            SvgMsgLogo.logo
          v-layout(align-start)
            .contact_info
              .contact_info__name
                SvgMsgLogo(v-if="ticketCreate")
                SvgMsgDollar(v-if="chat.type_id === chatTypes.special")
                span(v-if="ticketCreate") {{ $t('common.newTicket') }}
                span(v-else) {{ chatTitle }}
              .contact_info__shortmsg(v-if="!reconnectionAttempts")
                SvgMsgGroup(v-if="chat.type_id === chatTypes.group || chat.type_id === chatTypes.channel")
                span {{ (chat.type_id === chatTypes.group || chat.type_id === chatTypes.channel) ? $t('common.members', { number: chat.users_number }) : userActivityStatus }}
              .contact_info__shortmsg(v-else)
                span {{ $t('hints.connecting') }}
            v-layout(v-if="chat.contact && chat.contact.ref_role", align-center, justify-end)
              .contact_ref_role
                v-layout(align-center)
                  SvgReferralProp(:referral="chat.contact.ref_role === 'referral'")
                  span {{ $t(`common.${chat.contact.ref_role}`) }}
      v-flex
        v-layout
          v-menu(v-if="chat.type_id === chatTypes.group", nudge-bottom="50", nudge-left="60")
            template(v-slot:activator="{ on }")
              v-icon(
                size="32",
                v-on="on") mdi-dots-vertical
            .link_tooltip
              .link_tooltip__item(@click="showDialog = true") {{ chat.left_at ? $t('actions.joinChat') : $t('actions.leaveChat') }}
          .close(@click="onClickClose")
            SvgPlus
    v-dialog(v-model="showDialog", fullscreen, transition="dialog-bottom-transition")
      v-card.wrapper
        .dialog
          .dialog__title {{ chat.left_at ? $t('messages.confirm.joinChat') : $t('messages.confirm.leaveChat') }}
          p {{ chat.left_at ? $t('messages.confirm.joinChatDescription') : $t('messages.confirm.leaveChatDescription') }}
          v-layout.dialog__actions
            v-btn.text(:text="true", @click="onActionBtnClick") {{ chat.left_at ? $t('actions.joinChat') : $t('actions.leaveChat') }}
            v-btn(depressed, @click="showDialog = false") {{ $t('actions.cancel') }}
</template>

<script>
import { mapState } from 'vuex';
import ModerationMenu from '@/components/ModerationMenu';
import AvatarPlaceholder from '@/components/AvatarPlaceholder';
import SvgMsgDollar from '@/components/svg/SvgMsgDollar';
import SvgMsgGroup from '@/components/svg/SvgMsgGroup';
import SvgMsgLogo from '@/components/svg/SvgMsgLogo';
import SvgArrowThin from '@/components/svg/SvgArrowThin';
import SvgReferralProp from '@/components/svg/SvgReferralProp';
import SvgPlus from '@/components/svg/SvgPlus';
import { getAvatarClass } from '@/utils';
import CONSTANTS from '@/constants';
import { SET_ACTIVE_CHAT } from '@/store/modules/chats/types';

export default {
  components: {
    ModerationMenu,
    AvatarPlaceholder,
    SvgMsgDollar,
    SvgMsgGroup,
    SvgMsgLogo,
    SvgArrowThin,
    SvgReferralProp,
    SvgPlus
  },
  props: {
    ticketCreate: { type: Boolean, default: false },
    chat: { type: Object, required: true }
  },
  data() {
    return {
      getAvatarClass,
      showDialog: false,
      lastActiveDate: null,
      inactivePeriodInSec: null,
      minutesInterval: null,
      hoursInterval: null,
      chatTypes: CONSTANTS.chatTypes
    };
  },
  computed: {
    ...mapState('users', [
      'lastActivity'
    ]),
    ...mapState('chats', [
      'reconnectionAttempts'
    ]),
    userActivityData() {
      if (this.chat.type_id !== this.chatTypes.private || !this.chat.contact) return null;

      return this.lastActivity[this.chat.contact.id];
    },
    chatTitle() {
      switch (this.chat.type_id) {
        case 1:
        case 5:
          return this.chat.title;
        case 4:
          if (this.chat.contact) {
            return `${this.chat.contact.first_name} ${this.chat.contact.last_name}`;
          }
          return 'Private chat';
        default: return 'Unknown chat type';
      }
    },
    userActivityStatus() {
      if (!this.userActivityData) return '';

      if (this.userActivityData.online) {
        return this.$t('timing.online');
      }

      const minutes = Math.ceil(this.inactivePeriodInSec / 60);
      const hours = Math.trunc(minutes / 60);

      if (minutes < 60) {
        return this.$t('timing.lastSeenNMinutesAgo', { minutes });
      }

      clearInterval(this.minutesInterval);

      if (hours <= 24) {
        if (new Date(this.lastActiveDate).getDate() !== new Date().getDate()) {
          return this.$t('timing.lastSeenYesterday');
        }

        if (!this.hoursInterval) this.setHoursUpdateInterval();

        return this.$t('timing.lastSeenNHoursAgo', { hours });
      }

      clearInterval(this.hoursInterval);

      const diffInDays = Math.ceil(hours / 24);

      if (diffInDays < 7) {
        return this.$t('timing.lastSeenThisWeek');
      }

      if (diffInDays < 30) {
        const date = this.$options.filters.messageDate(this.lastActiveDate, this.$i18n.locale);

        return this.$t('timing.lastSeenOnDate', { date });
      }

      return this.$t('timing.lastSeenLongAgo');
    }
  },
  methods: {
    setActivityStatusDate(dateStr) {
      this.lastActiveDate = dateStr;
      this.inactivePeriodInSec = Math.trunc((Date.now() - new Date(dateStr).getTime()) / 1000);
    },
    onActionBtnClick() {
      this.$emit(this.chat.left_at ? 'clickJoinChat' : 'clickLeaveChat', this.chat.id);
      this.showDialog = false;
    },
    setMinutesUpdateInterval() {
      this.minutesInterval = setInterval(() => {
        this.inactivePeriodInSec += 60;
      }, 1000 * 60);
    },
    setHoursUpdateInterval() {
      this.hoursInterval = setInterval(() => {
        this.inactivePeriodInSec += 60 * 60;
      }, 1000 * 60 * 60);
    },
    setActivityStatusUpdateIntervals() {
      if ((this.inactivePeriodInSec / 60) < 60) {
        this.setMinutesUpdateInterval();
      } else if ((this.inactivePeriodInSec / 60 / 60) <= 24) {
        this.setHoursUpdateInterval();
      }
    },
    clearIntervals() {
      clearInterval(this.minutesInterval);
      clearInterval(this.hoursInterval);

      this.lastActiveDate = null;
      this.inactivePeriodInSec = null;
    },
    onClickClose() {
      this.$store.commit(`chats/${SET_ACTIVE_CHAT}`, null);
    }
  },
  watch: {
    chat(chat) {
      this.clearIntervals();

      if (chat.contact) {
        this.setActivityStatusDate(chat.contact.active_at);
        this.setActivityStatusUpdateIntervals();
      }
    },
    userActivityData: {
      handler(val) {
        if (val) this.setActivityStatusDate(val.activeAt);
      },
      deep: true
    }
  },
  mounted() {
    if (this.userActivityData) {
      this.setActivityStatusDate(this.userActivityData.activeAt);
      this.setActivityStatusUpdateIntervals();
    }
  },
  beforeDestroy() {
    this.clearIntervals();
  }
};
</script>

<style lang="scss" scoped>
  @import '../assets/scss/variables';

  .chat_header {
    background: $white;
    z-index: 1;
    width: 100%;

    .close {
      width: 34px;
      min-width: 34px;
      height: 34px;
      outline: none;
      transform: rotate(45deg);
      margin-left: 12px;
      cursor: pointer;

      svg { fill: $grey-light10; }

      @media screen and (max-width: 1024px) { display: none; }
    }

    .v-icon { color: $grey-light10; }

    @media screen and (max-width: 500px) {
      position: relative;
    }
  }

  .contact {
    &_item {
      width: 100%;
      padding: 16px 0 14px;
      display: flex;
      position: relative;
      align-items: center;

      & > .flex {
        flex-grow: 0;

        &:first-child { margin-left: 52px; }
      }

      &_leave {
        cursor: pointer;
        color: $blue;
        font-weight: 500;
      }

      &.special .contact_info__name { color: $orange-3; }

      @media screen and (max-width: 400px) {
        .contact_info {
          margin-left: 6px;

          &__name {
            font-size: 14px !important;

            span { max-width: 120px !important; }
          }
        }

        .contact_ref_role {
          font-size: 11px;
          padding: 3px;
        }

        .contact_preview > *,
        ::v-deep .v-avatar {
          min-width: 35px !important;
          width: 35px !important;
          height: 35px !important;

          span { font-size: 14px; }
        }
      }
    }

    &_back {
      align-items: center;
      cursor: pointer;
      display: none;

      svg {
        height: 32px;
        width: 32px;
        fill: $blue;
        margin-right: 8px;
      }
    }

    @media screen and (max-width: 1024px) {
      &_item > .flex:first-child { margin-left: 0; }

      &_back { display: flex; }
    }

    &_preview > * {
      width: 50px;
      height: 50px;
      background-size: cover;
      background-position: center;
      background-color: $light-theme-line;
      border-radius: 50%;
      min-width: 50px;
    }

    &_info {
      margin-left: 14px;
      max-width: 250px;
      width: 100%;
      position: relative;

      &__name {
        display: flex;
        align-items: center;
        line-height: normal;
        font-size: 16px;
        color: $sub-black;
        font-weight: 600;
        width: 100%;
        position: relative;

        &.logotype_icon .logotype_icon {
          display: block;
          fill: $orange-3;
        }

        &.group_icon .group_icon { display: block; }

        &.msg_icon .icon { display: block; }

        span {
          text-align: left;
          display: block;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          max-width: 250px;

          @media screen and (max-width: 550px) {
            max-width: 170px !important;
          }
        }

        svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
          margin-right: 9px;
        }
      }

      &__shortmsg {
        line-height: normal;
        font-size: 12px;
        text-align: left;
        margin-top: 8px;
        color: $light-theme-text;
        font-weight: 600;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;

        & > svg {
          width: 16px;
          height: 12px;
          fill: $light-theme-text;
          margin-right: 5px;
        }
      }
    }

    &_ref_role {
      background: $light-theme-line;
      padding: 5px;
      border-radius: 4px;
      font-weight: 500;
      font-size: 12px;
      white-space: nowrap;
      margin: -6px 0 0 5px;

      svg {
        width: 12px;
        height: 12px;
        margin-right: 8px;
      }
    }

    &_count {
      line-height: normal;
      font-size: 11px;
      text-align: center;
      color: $white;
      background: $error-color;
      font-weight: 600;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      position: absolute;
      top: 25px;
      right: -2px;
    }

    &_date {
      font-size: 16px;
      color: $light-theme-text;
      font-weight: 600;
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  svg.logo { fill: $orange-3; }

  .link_tooltip {
    background: $white;
    position: relative;

    &::before {
      content: '';
      width: 30px;
      height: 30px;
      background: $white;
      border-radius: 6px;
      position: absolute;
      top: -30px;
      transform: rotate(45deg);
      right: 40px;

      @media screen and (max-width: 690px) { right: 20px; }
    }

    &__item {
      color: $blue;
      display: flex;
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 10px;
      cursor: pointer;

      &:last-child { margin-bottom: 0; }

      svg {
        fill: $blue;
        width: 16px;
        height: 16px;
        margin-right: 13px;
        margin-top: 3px;
      }
    }
  }

  .v-menu {
    z-index: 20;

    &__content {
      border-radius: 15px;
      background: $white;
      padding: 20px 20px 15px;
      overflow: visible;
      contain: none;
      width: 100%;
      max-width: 150px;
      box-shadow: 0 0 6px $light-theme-05, 0 -10px 70px $light-theme-08;

      @media screen and (max-width: 690px) { max-width: 200px; }
    }

    &__activator--active span { color: $orange-3; }
  }

  .dialog {
    margin: auto;
    max-width: 550px;
    text-align: left;
    padding-top: 200px;

    &__title {
      font-size: 35px;
      font-weight: 700;
    }

    p {
      font-size: 20px;
      margin: 40px 0;
    }

    &__actions {
      .text {
        text-transform: none !important;
        color: $error-color;
        font-weight: 600;
        margin-right: 20px !important;
      }
    }
  }
</style>
